import type { StaffNotificationType } from "@/lib/staff-inbox";

export const STAFF_NOTIFICATION_TARGET_QUERY = {
  inquiry: "inquiryId",
  trialApplication: "applicationId",
  eventParticipant: "participantId",
  admissionRequest: "admissionRequestId",
  attendanceReport: "attendanceReportId",
  rescheduleRequest: "rescheduleRequestId",
  paymentHistory: "paymentHistoryId",
} as const;

export const STAFF_NOTIFICATION_FACILITY_QUERY = "workspaceFacility";

const FALLBACK_PATH: Record<StaffNotificationType, string> = {
  TRIAL_APPLICATION: "/trial-applications",
  EVENT_APPLICATION: "/events",
  EVENT_CANCELLATION: "/events",
  ADMISSION_REQUEST: "/admission/requests",
  ATTENDANCE_REPORT: "/attendance-reports",
  RESCHEDULE_REQUEST: "/reschedule-requests",
  INQUIRY: "/inquiries",
  PAYMENT_REFUND_REVIEW: "/billing/history",
  MEMBERSHIP_SUSPENSION_EXPIRY: "/billing/tuition",
  COURSE_ENROLLMENT_REQUEST: "/course-enrollment-requests",
};

const TARGET_DATA_TYPE: Record<StaffNotificationType, readonly string[]> = {
  TRIAL_APPLICATION: ["trial_application"],
  EVENT_APPLICATION: ["event_application"],
  EVENT_CANCELLATION: ["event_cancellation", "event_cancellation_request"],
  ADMISSION_REQUEST: ["admission_request"],
  ATTENDANCE_REPORT: ["attendance_report"],
  RESCHEDULE_REQUEST: ["reschedule_request"],
  INQUIRY: ["inquiry"],
  PAYMENT_REFUND_REVIEW: ["payment_refund_review"],
  MEMBERSHIP_SUSPENSION_EXPIRY: ["membership_suspension_expiry"],
  COURSE_ENROLLMENT_REQUEST: ["course_enrollment_request"],
};

type TargetData = Record<string, unknown>;

export type StaffNotificationDestination = {
  href: string;
  isDeepLink: boolean;
  notice?: {
    title: string;
    description: string;
  };
};

const asTargetData = (value: unknown): TargetData | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as TargetData)
    : null;

const readIdentifier = (data: TargetData, key: string): string | null => {
  const value = data[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (
    !trimmed ||
    trimmed.length > 200 ||
    /[\u0000-\u001f\u007f]/.test(trimmed)
  ) {
    return null;
  }
  return trimmed;
};

const withQuery = (
  pathname: string,
  query: Record<string, string | null>
): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const serialized = params.toString();
  return serialized ? `${pathname}?${serialized}` : pathname;
};

const fallback = (
  type: StaffNotificationType,
  description = "この通知には詳細を特定する情報がないため、対象の一覧を表示しました。"
): StaffNotificationDestination => ({
  href: FALLBACK_PATH[type],
  isDeepLink: false,
  notice: {
    title: "対象の一覧を表示しました",
    description,
  },
});

/**
 * API の targetData を、許可した管理画面内ルートだけへ変換する。
 * linkPath は旧API互換の表示用データとして扱い、遷移先の決定には使わない。
 */
export function resolveStaffNotificationDestination(input: {
  type: StaffNotificationType;
  targetData?: unknown;
}): StaffNotificationDestination {
  const data = asTargetData(input.targetData);
  if (!data || data.schemaVersion !== "1") return fallback(input.type);

  const dataType = readIdentifier(data, "type");
  if (!dataType || !TARGET_DATA_TYPE[input.type].includes(dataType)) {
    return fallback(input.type);
  }

  const facilityId = readIdentifier(data, "facilityId");
  const facilityQuery: Record<string, string | null> = {
    [STAFF_NOTIFICATION_FACILITY_QUERY]: facilityId,
  };

  switch (input.type) {
    case "MEMBERSHIP_SUSPENSION_EXPIRY":
    case "COURSE_ENROLLMENT_REQUEST":
      return fallback(
        input.type,
        "対象の一覧を表示しました。通知内容と一覧を照らし合わせてご確認ください。"
      );
    case "PAYMENT_REFUND_REVIEW": {
      const paymentHistoryId = readIdentifier(data, "paymentHistoryId");
      return paymentHistoryId
        ? {
            href: withQuery("/billing/history", {
              [STAFF_NOTIFICATION_TARGET_QUERY.paymentHistory]:
                paymentHistoryId,
              ...facilityQuery,
            }),
            isDeepLink: true,
          }
        : fallback(input.type);
    }
    case "INQUIRY": {
      const inquiryId = readIdentifier(data, "inquiryId");
      return inquiryId
        ? {
            href: withQuery("/inquiries", {
              [STAFF_NOTIFICATION_TARGET_QUERY.inquiry]: inquiryId,
              ...facilityQuery,
            }),
            isDeepLink: true,
          }
        : fallback(input.type);
    }
    case "TRIAL_APPLICATION": {
      const applicationId = readIdentifier(data, "applicationId");
      return applicationId
        ? {
            href: withQuery("/trial-applications", {
              [STAFF_NOTIFICATION_TARGET_QUERY.trialApplication]: applicationId,
              ...facilityQuery,
            }),
            isDeepLink: true,
          }
        : fallback(input.type);
    }
    case "EVENT_APPLICATION":
    case "EVENT_CANCELLATION": {
      const eventId = readIdentifier(data, "eventId");
      const participantId = readIdentifier(data, "participantId");
      if (!eventId) return fallback(input.type);
      const eventPath = `/events/${encodeURIComponent(eventId)}/participants`;
      if (!participantId) {
        return {
          ...fallback(
            input.type,
            "対象の申込者を特定できないため、イベントの申込者一覧を表示しました。"
          ),
          href: withQuery(eventPath, facilityQuery),
        };
      }
      return {
        href: withQuery(eventPath, {
          [STAFF_NOTIFICATION_TARGET_QUERY.eventParticipant]: participantId,
          ...facilityQuery,
        }),
        isDeepLink: true,
      };
    }
    case "ADMISSION_REQUEST": {
      const requestId = readIdentifier(data, "admissionRequestId");
      return requestId
        ? {
            href: withQuery("/admission/requests", {
              [STAFF_NOTIFICATION_TARGET_QUERY.admissionRequest]: requestId,
              ...facilityQuery,
            }),
            isDeepLink: true,
          }
        : fallback(input.type);
    }
    case "ATTENDANCE_REPORT": {
      const reportId = readIdentifier(data, "attendanceReportId");
      return reportId
        ? {
            href: withQuery("/attendance-reports", {
              [STAFF_NOTIFICATION_TARGET_QUERY.attendanceReport]: reportId,
              ...facilityQuery,
            }),
            isDeepLink: true,
          }
        : fallback(input.type);
    }
    case "RESCHEDULE_REQUEST": {
      const requestId = readIdentifier(data, "rescheduleRequestId");
      return requestId
        ? {
            href: withQuery("/reschedule-requests", {
              [STAFF_NOTIFICATION_TARGET_QUERY.rescheduleRequest]: requestId,
              ...facilityQuery,
            }),
            isDeepLink: true,
          }
        : fallback(input.type);
    }
  }
}

export const notificationTargetUnavailableToast = {
  title: "対象の詳細を開けませんでした",
  description:
    "対象が削除されているか、表示する権限がありません。一覧を表示しています。",
  variant: "destructive" as const,
};

export function removeNotificationTargetQuery(
  current: URLSearchParams,
  key: string
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(key);
  return next;
}

export function isStaffNotificationFacilityScopeReady(
  current: URLSearchParams,
  selectedFacilityId: string
): boolean {
  const requestedFacilityId = current.get(STAFF_NOTIFICATION_FACILITY_QUERY);
  return !requestedFacilityId || requestedFacilityId === selectedFacilityId;
}
